import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Stack,
  Tabs,
  Tab,
  Button,
  CircularProgress,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useSelector } from 'react-redux'

import GeneralTabContent from './GeneralTabContent'
import IntegrationsTabContent from './IntegrationsTabContent'
import PlansTabContent from './PlansTabContent'
import BillingTabContent from './BillingTabContent'
import PrivacyTabContent from './PrivacyTabContent'

import useGeneralSettings from '@/hooks/useGeneralSettings'

function TabContent({ selectedTab, generalSettingsHook }) {
  switch (selectedTab) {
    case 0:
      return <GeneralTabContent hook={generalSettingsHook} />
    case 1:
      return <IntegrationsTabContent />
    case 2:
      return <PlansTabContent />
    case 3:
      return <BillingTabContent />
    case 4:
      return <PrivacyTabContent />
    default:
      return null
  }
}

export default function UniversalSettingsModal({ open, onClose, defaultTab = 0 }) {
  const generalSettingsHook = useGeneralSettings()
  const profile = useSelector(({ user: { profile } }) => profile)
  const isTeacher = profile?.isTeacher || false

  const TAB_CONFIG = [
    {
      label: 'General',
      visible: true, // Always visible
      index: 0,
    },
    {
      label: 'Integrations',
      visible: isTeacher, // Only visible for teachers (will be overridden)
      index: 1,
    },
    {
      label: 'Plans',
      visible: isTeacher, // Only visible for teachers (will be overridden)
      index: 2,
    },
    {
      label: 'Billing',
      visible: isTeacher, // Only visible for teachers (will be overridden)
      index: 3,
    },
    {
      label: 'Privacy & Security',
      visible: false,
      index: 4,
    },
  ]

  const visibleTabs = TAB_CONFIG.filter(tab => tab.visible)

  // Find the visible tab index that corresponds to the defaultTab
  const getVisibleTabIndex = targetTabIndex => {
    const targetTab = TAB_CONFIG.find(tab => tab.index === targetTabIndex)

    if (!targetTab || !targetTab.visible) {
      return 0 // Fallback to first tab if target tab is not visible
    }

    return visibleTabs.findIndex(tab => tab.index === targetTabIndex)
  }

  const [selectedTabIndex, setSelectedTabIndex] = React.useState(() => getVisibleTabIndex(defaultTab))

  // Update selected tab when defaultTab or visibility changes
  React.useEffect(() => {
    const newVisibleTabIndex = getVisibleTabIndex(defaultTab)

    setSelectedTabIndex(newVisibleTabIndex)
  }, [defaultTab, isTeacher])

  // Reset to first tab if current selection is out of bounds
  React.useEffect(() => {
    if (selectedTabIndex >= visibleTabs.length) {
      setSelectedTabIndex(0)
    }
  }, [selectedTabIndex, visibleTabs.length])

  const handleClose = () => {
    onClose()
  }

  const handleTabChange = (event, newValue) => {
    setSelectedTabIndex(newValue)
  }

  const handleSaveAndClose = async () => {
    const currentTab = visibleTabs[selectedTabIndex]

    if (currentTab?.index === 0) {
      await generalSettingsHook.handleSave()
    }
  }

  const getSelectedTabContentIndex = () => {
    const currentTab = visibleTabs[selectedTabIndex]

    return currentTab?.index || 0
  }

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      sx={{
        '& .MuiPaper-root': {
          maxWidth: '1260px',
          borderRadius: '16px',
          background: '#FFF',
          boxShadow: '0px 12px 16px -4px rgba(16, 24, 40, 0.08), 0px 4px 6px -2px rgba(16, 24, 40, 0.03)',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '90vh',
          height: '100%',
        },
      }}
      onClose={handleClose}
    >
      <DialogTitle
        component="div"
        sx={{
          display: 'flex',
          padding: '24px 24px 20px 24px',
          borderBottom: '1px solid #EAECF0',
          flexShrink: 0,
        }}
      >
        <Stack gap="4px">
          <Typography color="#101828" fontSize={18} fontWeight={600} lineHeight="28px">
            Settings
          </Typography>
          <Typography color="#475467" lineHeight="20px">
            Manage your profile and preferences here.
          </Typography>
        </Stack>
        <IconButton
          sx={{
            position: 'absolute',
            right: '24px',
            top: '24px',
          }}
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flex: 1 }}>
        <Box sx={{ display: 'flex', width: '100%', height: '100%' }}>
          {/* Sticky Tabs Sidebar */}
          <Box
            sx={{
              width: '210px',
              borderRight: '1px solid #EAECF0',
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              position: 'fixed',
              height: '72vh',
            }}
          >
            <Tabs
              TabIndicatorProps={{ style: { display: 'none' } }}
              orientation="vertical"
              sx={{
                p: 2,
                height: '100%',
                '& .MuiTab-root': {
                  alignItems: 'flex-start',
                  fontSize: '16px',
                  fontWeight: 600,
                  lineHeight: '24px',
                  color: '#667085',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  minHeight: '48px',
                },
                '& .Mui-selected': {
                  color: '#A12F7E',
                  background: '#FDF2FA',
                },
                '& .MuiTabs-flexContainer': {
                  gap: '4px',
                  flexDirection: 'column',
                  height: '100%',
                },
                '& .MuiTabs-scroller': {
                  height: '100%',
                },
              }}
              value={selectedTabIndex}
              onChange={handleTabChange}
            >
              {visibleTabs.map(tab => (
                <Tab key={tab.label} label={tab.label} />
              ))}
            </Tabs>
          </Box>

          {/* Scrollable Content Area */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              marginLeft: '210px',
            }}
          >
            <Box sx={{ p: 2, flex: 1 }}>
              <TabContent generalSettingsHook={generalSettingsHook} selectedTab={getSelectedTabContentIndex()} />
            </Box>
          </Box>
        </Box>
      </DialogContent>
      {selectedTabIndex === 0 && (
        <DialogActions
          sx={{
            p: '20px 32px 16px 32px',
            gap: 2,
            justifyContent: 'flex-end',
            flexShrink: 0,
            borderTop: '1px solid #EAECF0',
          }}
        >
          <Button
            sx={{
              borderRadius: 2,
              minWidth: 100,
              borderColor: '#D1D5DB',
              color: '#222',
              '&:hover': {
                background: '#f5f5f5',
                borderColor: '#BDBDBD',
              },
            }}
            variant="outlined"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            disabled={generalSettingsHook.loading}
            startIcon={generalSettingsHook.loading ? <CircularProgress size={20} /> : null}
            sx={{
              borderRadius: 2,
              minWidth: 100,
              bgcolor: '#F73CA4',
              '&:hover': { bgcolor: '#d92b8c' },
              '&:disabled': {
                bgcolor: '#E5E7EB',
                color: '#9CA3AF',
              },
            }}
            variant="contained"
            onClick={handleSaveAndClose}
          >
            Save
          </Button>
        </DialogActions>
      )}
    </Dialog>
  )
}
